/**
 * Dar al paciente acceso al portal, desde su ficha.
 *
 * La clave se enseña UNA vez y en grande, porque alguien la va a dictar en voz
 * alta en un mostrador con ruido. No se guarda en claro en ningún sitio: si se
 * pierde, se genera otra desde aquí mismo.
 *
 * Cuando el sistema usa el documento como contraseña —lo normal hoy— ni
 * siquiera hay nada que dictar, y el texto lo dice tal cual: "entra con su DNI
 * como usuario y como contraseña".
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Ban, Check, Copy, Smartphone } from "lucide-react";
import {
  type AccesoPortal,
  type Paciente,
  darAccesoPortal,
  quitarAccesoPortal,
} from "../../api/pacientes";
import { formatApiError } from "../../api/client";
import { useBase } from "../../auth/TenantBrandingProvider";
import { Alert, Button, Card, CardBody, CardHeader, ConfirmDialog } from "../ui";

export function AccesoPortalCard({
  paciente,
  puedeEditar,
}: {
  paciente: Paciente;
  puedeEditar: boolean;
}) {
  const base = useBase();
  const [resultado, setResultado] = useState<AccesoPortal | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiado, setCopiado] = useState(false);

  const acceso = paciente.acceso_portal;
  const queryClient = useQueryClient();
  const [porQuitar, setPorQuitar] = useState(false);

  const quitar = useMutation({
    mutationFn: () => quitarAccesoPortal(paciente.id),
    onSuccess: () => {
      setPorQuitar(false);
      setResultado(null);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["paciente", paciente.id] });
      queryClient.invalidateQueries({ queryKey: ["pacientes"] });
    },
    onError: (e) => {
      setPorQuitar(false);
      setError(formatApiError(e, "No se pudo quitar el acceso."));
    },
  });

  const dar = useMutation({
    mutationFn: () => darAccesoPortal(paciente.id),
    onSuccess: (r) => {
      setResultado(r);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["paciente", paciente.id] });
    },
    onError: (e) => {
      setResultado(null);
      setError(formatApiError(e, "No se pudo dar el acceso."));
    },
  });

  if (!puedeEditar) return null;

  // La dirección que hay que dictarle. Se arma con el mismo slug con el que
  // está entrando el personal, así que en un cliente con dominio propio sale
  // el suyo y no el nuestro.
  const url = `${window.location.origin}${base}/portal`;

  async function copiar() {
    await navigator.clipboard.writeText(url);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="flex items-center gap-2 font-semibold text-slate-900">
              <Smartphone className="h-4 w-4 text-[color:var(--color-link)]" />
              Portal del paciente
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Para que consulte sus citas, recetas y resultados desde su
              teléfono.
            </p>
          </div>
          {/* El botón dice lo que toca según cómo esté.

              Antes había uno solo, "Dar acceso", y era el paso que nadie
              recordaba: un paciente registrado en recepción no podía entrar a
              la app y nada en la ficha explicaba por qué. Ahora la cuenta nace
              con el registro; lo que queda aquí es restablecer la clave —lo que
              de verdad pide quien vuelve al mostrador diciendo "no puedo
              entrar"— y quitar el acceso, que sí es una excepción. */}
          <div className="flex flex-wrap items-center gap-2">
            {acceso.tiene_cuenta && acceso.activo && (
              <Button variant="ghost" onClick={() => setPorQuitar(true)}>
                <Ban className="h-4 w-4" />
                Quitar acceso
              </Button>
            )}
            {!acceso.impedimento && (
              <Button
                variant="ghost"
                onClick={() => dar.mutate()}
                loading={dar.isPending}
              >
                {!acceso.tiene_cuenta
                  ? "Dar acceso"
                  : acceso.activo
                    ? "Restablecer clave"
                    : "Devolver el acceso"}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        {error && <Alert>{error}</Alert>}

        {/* El estado, antes que cualquier botón: quien abre esta ficha suele
            venir de "no puedo entrar a la app". */}
        {acceso.impedimento ? (
          <Alert variant="info">{acceso.impedimento}</Alert>
        ) : acceso.tiene_cuenta && !acceso.activo ? (
          <Alert variant="info">
            Le quitaron el acceso. Puede seguir atendiéndose con normalidad: lo
            único que no puede es entrar a la app.
          </Alert>
        ) : acceso.tiene_cuenta ? (
          <p className="text-sm text-slate-600">
            Entra con su documento{" "}
            <span className="font-mono font-medium text-slate-900">
              {paciente.num_documento}
            </span>
            .
          </p>
        ) : null}

        {resultado && (
          <div className="rounded-lg border border-[color:var(--color-link)] bg-[color-mix(in_srgb,var(--color-link)_6%,white)] p-4">
            <p className="text-sm font-medium text-slate-900">
              {resultado.ya_tenia_cuenta
                ? "Acceso restablecido"
                : "Acceso creado"}
            </p>

            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex flex-wrap items-baseline gap-2">
                <dt className="w-24 text-slate-500">Usuario</dt>
                <dd className="font-mono text-lg font-semibold text-slate-900">
                  {resultado.num_documento}
                </dd>
              </div>
              <div className="flex flex-wrap items-baseline gap-2">
                <dt className="w-24 text-slate-500">Contraseña</dt>
                <dd className="font-mono text-lg font-semibold text-slate-900">
                  {/* Con el documento como clave no hay nada que dictar: se
                      dice así, en vez de repetir el número dos veces. */}
                  {resultado.password_temporal === resultado.num_documento
                    ? "la misma (su documento)"
                    : resultado.password_temporal}
                </dd>
              </div>
            </dl>

            <p className="mt-3 text-xs text-slate-600">
              Dile que entre a <span className="font-medium">{url}</span> y que
              cambie la contraseña desde "Mis datos".
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
          <span className="font-mono text-xs">{url}</span>
          <button
            type="button"
            onClick={copiar}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-[color:var(--color-link)] hover:bg-slate-100"
          >
            {copiado ? (
              <>
                <Check className="h-3.5 w-3.5" />
                Copiada
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copiar dirección
              </>
            )}
          </button>
        </div>
      </CardBody>

      <ConfirmDialog
        open={porQuitar}
        onClose={() => setPorQuitar(false)}
        onConfirm={() => quitar.mutate()}
        loading={quitar.isPending}
        title="Quitar el acceso a la app"
        description="Dejará de poder entrar a ver sus citas y recetas desde el teléfono. Se le atiende igual, y puedes devolvérselo cuando quieras."
        detalle={<>{paciente.nombre_completo}</>}
        confirmLabel="Sí, quitárselo"
        cancelLabel="No, dejarlo"
      />
    </Card>
  );
}
